import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarX, Clock, Image, Save, Store } from "lucide-react";
import { Suspense, useState } from "react";

import { ApiError } from "@/api/errors";
import {
  businessHoursQuery,
  createBusinessHours,
  establishmentQuery,
  logoQuery,
  type PaymentMethodCode,
  paymentMethodsQuery,
  updateEstablishment,
} from "@/api/establishment";
import { professionalsQuery } from "@/api/professionals";
import { scheduleExceptionsQuery } from "@/api/schedule-exceptions";
import { warmQueries } from "@/api/warm";
import { useAuth } from "@/auth/AuthProvider";
import {
  type DaySchedule,
  initialWeek,
  syncWeekSchedule,
  WeekScheduleGrid,
} from "@/components/business-hours/week-schedule";
import { ExcecoesSection } from "@/components/configuracoes/ExcecoesSection";
import { FormasPagamentoField } from "@/components/configuracoes/FormasPagamentoField";
import { LogoSection } from "@/components/configuracoes/LogoSection";
import { SettingsCard } from "@/components/configuracoes/SettingsCard";
import { PageActions } from "@/components/page-actions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { notifySuccess } from "@/lib/feedback";

export const Route = createFileRoute("/_authed/configuracoes/salao")({
  loader: ({ context: { queryClient, establishmentId } }) =>
    warmQueries(queryClient, [
      establishmentQuery(establishmentId),
      businessHoursQuery(establishmentId),
      logoQuery(establishmentId),
      // As exceções de calendário são de uma seção interna, mas entram aqui
      // pelo mesmo motivo das outras: o loader é o único ponto que roda
      // ANTES da tela montar.
      scheduleExceptionsQuery(establishmentId),
      professionalsQuery(establishmentId),
      paymentMethodsQuery(),
    ]),
  staticData: {
    title: "Estabelecimento",
    subtitle: "Nome, endereço, horário de funcionamento e feriados.",
  },
  component: SalaoPage,
});

function SalaoPage() {
  const { user } = useAuth();
  // `_authed` só renderiza autenticado — user nunca é null aqui.
  if (!user) return null;
  return (
    <Suspense
      fallback={
        // Alturas próximas das dos cartões de verdade: um esqueleto de
        // tamanho diferente do conteúdo empurra a página quando o dado
        // chega, e o salto lê como lentidão mesmo quando foi rápido.
        <div className="space-y-6">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
      }
    >
      <SalaoForm establishmentId={user.establishment_id} />
    </Suspense>
  );
}

/**
 * Dados do salão e grade semanal salvam JUNTOS, pelo botão da toolbar — por
 * isso os dois moram no mesmo grupo e no mesmo componente. Exceções de
 * calendário estão aqui por afinidade (as três respondem "quando o salão
 * abre"), mas salvam sozinhas, na hora.
 */
function SalaoForm({ establishmentId }: { establishmentId: string }) {
  const queryClient = useQueryClient();
  const { data: establishment } = useSuspenseQuery(establishmentQuery(establishmentId));
  const { data: hours } = useSuspenseQuery(businessHoursQuery(establishmentId));

  const [name, setName] = useState(establishment.name);
  const [address, setAddress] = useState(establishment.address ?? "");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodCode[]>(
    establishment.payment_methods ?? [],
  );
  const [week, setWeek] = useState<DaySchedule[]>(() => initialWeek(hours));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toda edição limpa o erro da toolbar: ele fica longe do campo que o
  // causou (um dia da semana pode estar a uma tela de distância), e mantê-lo
  // depois da correção fazia parecer que o problema continuava.
  const updateDay = (dayOfWeek: number, patch: Partial<DaySchedule>) => {
    setError(null);
    setWeek((w) => w.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)));
  };

  /** Grava um intervalo por dia: cria, atualiza ou remove conforme o diff. */
  async function syncBusinessHours() {
    // Sem `professionalId`: o sync só olha as linhas do estabelecimento.
    await syncWeekSchedule(week, hours, undefined, (body) =>
      createBusinessHours(establishmentId, body),
    );
  }

  async function onSave() {
    setError(null);
    if (!name.trim()) {
      setError("Informe o nome do estabelecimento.");
      return;
    }
    const invalidDay = week.find((d) => d.open && d.start >= d.end);
    if (invalidDay) {
      setError(`Na ${invalidDay.label}, a abertura precisa ser antes do fechamento.`);
      return;
    }
    setSaving(true);
    try {
      // Strings vazias apagam o campo na API (regra documentada do update
      // parcial) — é o que a tela quer dizer quando o dono limpa o campo.
      await updateEstablishment(establishmentId, {
        name: name.trim(),
        address: address.trim(),
        payment_methods: paymentMethods,
      });
      await syncBusinessHours();
      await queryClient.invalidateQueries({
        queryKey: ["establishment", establishmentId],
      });
      notifySuccess("Dados do estabelecimento salvos");
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Não foi possível salvar. Tente de novo.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageActions>
        {error && (
          // `hidden sm:block`: no celular o cabeçalho é uma linha de 56px e
          // não comporta um parágrafo. O mesmo erro aparece no corpo da tela
          // (ver abaixo), que é onde o polegar já está.
          <p role="alert" className="hidden text-xs text-destructive sm:block">
            {error}
          </p>
        )}
        <Button size="sm" className="gap-1.5" onClick={onSave} disabled={saving}>
          <Save className="h-4 w-4" />
          {/* Dois rótulos inteiros, e não "Salvar" + um sufixo em `span`: com o
              sufixo o JSX deixava um espaço a mais entre as duas partes no
              desktop ("Salvar  alterações"). */}
          <span className="sm:hidden">{saving ? "Salvando…" : "Salvar"}</span>
          <span className="hidden sm:inline">
            {saving ? "Salvando…" : "Salvar alterações"}
          </span>
        </Button>
      </PageActions>

      <div className="space-y-6">
        {/* O gêmeo mobile do erro do cabeçalho: lá ele é `hidden sm:block`
            porque a barra de 56px do celular não comporta um parágrafo, aqui
            ele é `sm:hidden`. Um dos dois sempre aparece — nunca os dois. */}
        {error && (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive sm:hidden"
          >
            {error}
          </p>
        )}
        <SettingsCard
          icon={Store}
          title="Dados do estabelecimento"
          contentClassName="grid gap-4 sm:grid-cols-2"
        >
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="nome">Nome do estabelecimento</Label>
            <Input
              id="nome"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="endereco">Endereço</Label>
            <Input
              id="endereco"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                setError(null);
              }}
              maxLength={300}
              placeholder="O bot responde isso quando o cliente pergunta onde fica"
            />
          </div>
          <Suspense fallback={<Skeleton className="h-20 w-full sm:col-span-2" />}>
            <FormasPagamentoField
              value={paymentMethods}
              onChange={(next) => {
                setPaymentMethods(next);
                setError(null);
              }}
            />
          </Suspense>
        </SettingsCard>

        {/* Salva sozinha, na hora — escolher o arquivo já é a confirmação.
            Fronteira própria pelo mesmo motivo das exceções: é mais uma
            consulta, e prendê-la junto atrasaria nome e endereço. */}
        <SettingsCard
          icon={Image}
          title="Logo do estabelecimento"
          description="A imagem que o seu cliente vê depois de pagar o sinal."
        >
          <Suspense fallback={<Skeleton className="h-24 w-full" />}>
            <LogoSection establishmentId={establishmentId} />
          </Suspense>
        </SettingsCard>

        <SettingsCard
          icon={Clock}
          title="Horário de funcionamento"
          description="A agenda e o bot só oferecem horários dentro destes períodos."
          contentClassName="px-0 pb-3"
        >
          <div className="px-6">
            <WeekScheduleGrid week={week} onChange={updateDay} />
          </div>
        </SettingsCard>

        {/* Criar/remover é imediato, fora do "Salvar alterações" (que cobre
            só dados do salão + grade semanal). */}
        <SettingsCard
          icon={CalendarX}
          title="Exceções de calendário"
          description="Feriado, folga ou horário especial numa data — vale para todo o estabelecimento ou só para um profissional."
        >
          {/* Fronteira PRÓPRIA, e é o que faz esta tela abrir rápido: as
              exceções pedem mais duas consultas (a lista e os
              profissionais), e numa fronteira só com o resto a página
              inteira ficava esperando a mais lenta das quatro. Com a
              fronteira aqui, nome, endereço e a grade da semana aparecem
              assim que as DUAS consultas deles respondem, e a lista de
              exceções preenche depois, no lugar dela. */}
          <Suspense fallback={<Skeleton className="h-40 w-full" />}>
            <ExcecoesSection establishmentId={establishmentId} />
          </Suspense>
        </SettingsCard>
      </div>
    </>
  );
}
